//src/lib/axios.js
import { useNotificationStore } from 'stores/notifications';
import storage from 'utils/storage';
import Axios from 'axios';
import PropTypes from 'prop-types';

import { API_URL } from 'config';
import { BUSINESS_API_URL } from 'config';
import { NOTIFICATIONS_API_URL } from 'config';
import { PAYMENTS_API_URL } from 'config';
import { RIDE_HAILING_API_URL } from 'config';
import { MESSAGING_API_URL } from 'config';

function authRequestInterceptor(config) {
  const token = storage.getToken();

  if (token) {
    config.headers.authorization = `Bearer ${token}`;
  }

  config.headers.Accept = 'application/json';
  return config;
}

authRequestInterceptor.propTypes = {
  config: PropTypes.any,
};

// Shared response interceptor
function responseInterceptor(response) {
  return response.data;
}

function errorInterceptor(error) {
  console.log(error);
  const errorMsg = error.response?.data?.message || error.response?.data?.error;
  useNotificationStore.getState().addNotification({
    type: 'error',
    title: 'Something went wrong',
    message: errorMsg ? errorMsg : 'Please check server connection',
  });

  return Promise.reject(error);
}

// ✅ Default instance using API_URL
const axios = Axios.create({
  baseURL: API_URL,
  timeout: 50000,
  headers: {
    post: {
      'Content-Type': 'multipart/form-data',
    },
  },
});

axios.interceptors.request.use(authRequestInterceptor);
axios.interceptors.response.use(responseInterceptor, errorInterceptor);

// 📦 Utility for generating new instances dynamically
axios.withBaseURL = (baseURL, config = {}) => {
  const instance = Axios.create({
    baseURL,
    timeout: 50000,
    headers: {
      post: {
        'Content-Type': 'application/json',
      },
      ...(config.headers || {}),
    },
    ...config,
  });

  instance.interceptors.request.use(authRequestInterceptor);
  instance.interceptors.response.use(responseInterceptor, errorInterceptor);

  return instance;
};

// 🧠 Microservice-aware instance registry
axios.micro = {
  _services: {},

  register(serviceKey, baseURL) {
    this._services[serviceKey] = baseURL;
  },

  use(serviceKey) {
    const baseURL = this._services[serviceKey];
    if (!baseURL) {
      throw new Error(`Microservice "${serviceKey}" is not registered.`);
    }
    return axios.withBaseURL(baseURL);
  },
};

axios.micro.register('businesses', BUSINESS_API_URL);
axios.micro.register('notifications', NOTIFICATIONS_API_URL);
axios.micro.register('payments', PAYMENTS_API_URL);
axios.micro.register('ride_hailing', RIDE_HAILING_API_URL);
axios.micro.register('messaging', MESSAGING_API_URL);

const businesses = axios.micro.use('businesses');
const notifications = axios.micro.use('notifications');
const ride_hailing = axios.micro.use('ride_hailing');
const payments = axios.micro.use('payments');
const messaging = axios.micro.use('messaging');

export { axios, businesses, notifications, ride_hailing, payments, messaging };
